import {gql, useQuery} from '@apollo/client';
import {ValidatedTextInputField} from 'common';
import {useDebounce} from 'common/hook';
import {Pane, Popover, Position, TextInputField} from 'evergreen-ui';
import React from 'react';
import {useFormContext} from 'react-hook-form';
import {useCombobox} from 'downshift';
import {OfferFormInput} from "../../../../offer/component/OfferForm";
import {Observable} from "rxjs";
import {useObservable} from "../../../../rxsupport";
import {ServiceType} from "referral";
import {intersection} from "lodash";

type Provider = {
    id: string;
    guid: string;
    name: string;
    sfId: string;
    isReferralsOnHold: boolean;
    contractedServiceTypes: string[] | null;
    contractedServiceAreas: string[] | null;
};
const isProviderProvidedPreferredServiceArea = (provider: Provider, preferredServiceArea: string | null) => {
    if (preferredServiceArea === null ||
        provider.contractedServiceAreas === null ||
        (provider.contractedServiceAreas && provider.contractedServiceAreas.length === 0)) {
        return false;
    }

    return provider.contractedServiceAreas
        .map((area) => area.trim().toLowerCase())
        .includes(preferredServiceArea);
}

type ProvidersResult = {
    providers: Provider[];
};

export const PROVIDERS_BY_REFERRAL_QUERY = gql`
query providerForIum($name: String) {
  providers(name: $name) {
    id
    guid
    sfId
    name
    isReferralsOnHold
    contractedServiceTypes
    contractedServiceAreas
  }
}
`;

type AutocompleteProviderByReferralInputProps = {
    referralPreferredLocation: string | null;
    referralPreferredServiceTypes: ServiceType[];
    isSubmitting$: Observable<boolean>;
    excludedProviderSfIds: string[];
};
export const AutocompleteProviderByReferralInput = ({
                                                        referralPreferredLocation,
                                                        referralPreferredServiceTypes,
                                                        isSubmitting$,
                                                        excludedProviderSfIds,
                                                    }: AutocompleteProviderByReferralInputProps) => {
    const [filerVal, setFilterVal] = React.useState("");
    const debounceValue = useDebounce(filerVal, 300);
    const {
        setValue,
        formState: {
            errors,
        },
    } = useFormContext();
    const [inputItems, setInputItems] = React.useState<Array<Provider>>([]);
    const inputRef = React.useRef<HTMLInputElement>(null);
    const isSubmitting = useObservable(isSubmitting$, false);

    const {loading} = useQuery<ProvidersResult, {}>(PROVIDERS_BY_REFERRAL_QUERY, {
        variables: {
            name: debounceValue,
        },
        onCompleted: (data: ProvidersResult) => {
            if (data && data.providers) {
                setInputItems(data.providers)
            }
            inputRef.current?.focus();
        },
        fetchPolicy: 'cache-first',
        skip: debounceValue.length < 2,
    });


    const {
        isOpen,
        setInputValue,
        selectedItem,
        getMenuProps,
        getInputProps,
        getComboboxProps,
        highlightedIndex,
        getItemProps,
    } = useCombobox({
        items: inputItems,
        itemToString: (item) => item.name,
        onInputValueChange: ({inputValue}) => {
            setFilterVal(inputValue ?? "");
        },
        onSelectedItemChange: ({selectedItem}) => {
            if (selectedItem) {
                setValue("providerGuid", selectedItem.guid, {shouldValidate: true});
            }
        },
    })


    return (
        <Pane>
            <Popover
                isShown={isOpen}
                position={Position.BOTTOM_LEFT}
                onClose={() => {
                    // use for show current selected item, if input value empty clear input value
                    if (selectedItem && filerVal !== "" && inputItems.length !== 0) {
                        setInputValue(selectedItem.name);
                    } else {
                        setInputValue("");
                        setValue("providerGuid", null);
                    }
                }}
                content={
                    <Pane width={320}>
                        {isOpen && loading &&
                            <Pane paddingY={4} paddingLeft={15} className="loading-text">Loading</Pane>}
                        {isOpen &&
                            inputItems.map((item, index) => (
                                <Pane
                                    paddingY={4}
                                    whiteSpace={"nowrap"}
                                    paddingLeft={15}
                                    overflow={'hidden'}
                                    textOverflow={'ellipsis'}
                                    backgroundColor={highlightedIndex === index ? '#bde4ff' :
                                        selectedItem && selectedItem.guid === item.guid ? '#a0aec0' : '#fff'}
                                    key={`${item.guid}${index}`}
                                    {...getItemProps({item, index})}
                                >
                                    {item.name}
                                </Pane>
                            ))}
                        {isOpen && !loading && inputItems.length === 0 &&
                            <Pane paddingY={4} paddingLeft={15}>Not found</Pane>}
                    </Pane>
                }

                {...getMenuProps()}
            >
                <Pane>
                    <div {...getComboboxProps()}>
                        <TextInputField
                            label={'Provider'}
                            size={400}
                            inputWidth={'24rem'}
                            placeholder="Provider"
                            isInvalid={!!errors.providerGuid}
                            disabled={isSubmitting}
                            data-testid='um-autocomplete-provider-input'
                            className={loading ? 'loading-cursor' : ''}
                            {...getInputProps({
                                ref: inputRef,
                                onChange: (e: any) => {
                                    if (e.target && e.target.value === "") {
                                        setInputValue("");
                                        setValue("providerGuid", null);
                                    }
                                },
                            })}
                        />
                    </div>
                </Pane>
            </Popover>
            <ValidatedTextInputField<OfferFormInput>
                label={""}
                className='ev-overwrite ev-input-hidden'
                width={'24rem'}
                size={400}
                marginTop='-10px'
                name='providerGuid'
                data-testid='um-autocomplete-provider-input-guid'
                validationRules={{
                    required: 'This field is required',
                    validate: () => {
                        if (!selectedItem) {
                            return 'Please select a provider';
                        }
                        if (excludedProviderSfIds.includes(selectedItem.sfId)) {
                            return 'Selected provider is excluded from the referral';
                        }
                        if (selectedItem.isReferralsOnHold) {
                            return 'Selected provider has referrals on hold';
                        }
                        if (!isProviderProvidedPreferredServiceArea(selectedItem, referralPreferredLocation)) {
                            return 'Selected provider does not service the referral location';
                        }
                        if (intersection(selectedItem.contractedServiceTypes, referralPreferredServiceTypes).length === 0) {
                            return 'Selected provider does not service the referral service type';
                        }
                        return true;
                    }
                }}
            />
        </Pane>
    );
}
